import { bool, node, number, oneOfType, string } from "prop-types";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";
import {
  color,
  cursor,
  fontSize,
  opacity,
  themeSizes,
  themeVariants,
} from "theme";

import FnrTypography from "FnrTypography";

import FnrBadge from "UI/FnrBadge";
import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrIcon from "UI/FnrIcon";
import { FnrToggleChild } from "UI/FnrToggle2";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";
import { TabBackground } from "UI/tabBarStyles";

import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";

import { FnrTabsContext } from "./FnrTabs";

const SFnrHorizontalGap = styled(FnrHorizontalGap)`
  ${cursor}
  ${opacity}
`;

const SDiv = styled("div")`
  ${color}
  ${fontSize}
  transition: color .3s;
`;

const TypographyRelay = ({
  children,
  icon,
  isHoverFocused,
  isSelected,
  amount,
  disabled,
}) => {
  const fontColor = useMemo(() => {
    if (isSelected) return "fnDarkestBlueGrey";
    if (isHoverFocused) return "fnBlue";
    return "textSecondaryDark";
  }, [isHoverFocused, isSelected]);
  if (typeof children === "string")
    return (
      <SFnrHorizontalGap
        gap={6}
        $variant={disabled ? themeVariants.disabled : undefined}
        $opacity={disabled ? ".6" : "1"}
      >
        {Boolean(icon) && (
          <SDiv $size={themeSizes.md} $color={fontColor}>
            <FnrIcon icon={icon} />
          </SDiv>
        )}
        <FnrTypography fontSize="lg" color={fontColor}>
          {children}
        </FnrTypography>
        {Boolean(amount) && <FnrBadge color={fontColor}>{amount}</FnrBadge>}
      </SFnrHorizontalGap>
    );
  return children;
};

TypographyRelay.propTypes = {
  children: oneOfType([node, string]).isRequired,
  icon: string,
  isHoverFocused: bool,
  isSelected: bool,
  amount: number,
  disabled: bool,
};

const Tab = ({ tabKey, label, icon, amount, disabled }) => {
  const { handleSetActiveTab, activeKey } = useContext(FnrTabsContext);
  const handleClick = () => {
    if (!disabled) handleSetActiveTab(tabKey);
  };

  const [selectionAllowed, setSelectionAllowed] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      // This is a hack around HoverParentInside. It needs to just work. For now, we'll just delay the init selection. Otherwise, the divider prop throws it off.
      setSelectionAllowed(true);
    }, 100);
  }, []);

  const isSelected = useMemo(() => {
    return selectionAllowed && tabKey === activeKey;
  }, [activeKey, selectionAllowed, tabKey]);

  const ref = useRef();
  const focusedElem = useIsHoverFocused(ref);

  return (
    <FnrButtonWrapper ref={ref} onClick={handleClick} disabled={disabled} data-cy={`FnrTabs_Tab_${tabKey}`}>
      <FnrToggleChild selected={isSelected} disabled={disabled}>
        <TabBackground disabled={disabled}>
          <TypographyRelay
            isHoverFocused={Boolean(focusedElem) && !disabled}
            isSelected={isSelected}
            icon={icon}
            amount={amount}
            disabled={disabled}
          >
            {label}
          </TypographyRelay>
        </TabBackground>
      </FnrToggleChild>
    </FnrButtonWrapper>
  );
};

Tab.propTypes = {
  tabKey: string.isRequired,
  label: oneOfType([node, string]).isRequired,
  icon: string,
  amount: number,
  disabled: bool,
};

export default Tab;
